import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { toast } from 'day8-ui';
import {
  Svg
} from 'day8-react/build/new-components';

import EditProfileSection from './EditProfileSection';
import EditContactSection from './EditContactSection';
import EditAddressSection from './EditAddressSection';
import { updateUser } from '../../../actions/user';
import { fetchAccountData } from '../../../actions/account';
import { validatePassport, validatePhone, validateNotRedacted } from '../../../utils/profileValidation';
import { scrollToFirstFieldError } from '../../../utils/scrollToField';
import { peekProfileEditCache, setProfileEditCache } from '../../../helpers/profileEditCache';

const profileToFormData = (profile, token) => ({
  ...profile,
  birthDate: {
    day: profile.birthDate ? moment.utc(profile.birthDate).date() : '',
    month: profile.birthDate ? moment.utc(profile.birthDate).month() + 1 : '',
    year: profile.birthDate ? moment.utc(profile.birthDate).year() : ''
  },
  _token: token
});

const flattenFormErrors = (formErrors, prefix = '') => {
  const messages = [];
  if (!formErrors || typeof formErrors !== 'object') {
    return messages;
  }
  Object.entries(formErrors).forEach(([key, value]) => {
    if (key === 'summaryMessage') {
      return;
    }
    const field = prefix || key;
    if (typeof value === 'string') {
      messages.push({ field: '_form', message: value });
      return;
    }
    if (Array.isArray(value) && typeof value[0] === 'string') {
      messages.push({ field, message: value[0] });
      return;
    }
    if (value && typeof value === 'object') {
      messages.push(...flattenFormErrors(value, field));
    }
  });
  return messages;
};

const ProfileEditForm = ({ onSuccess, initialProfile = null, initialToken = null }) => {

  const dispatch = useDispatch();
  const [userFormData, setUserFormData] = useState({ birthDate: {} });
  const [loadingData, setLoadingData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  useEffect(() => {
    if (initialProfile && initialToken) {
      setProfileEditCache(initialProfile, initialToken);
      setUserFormData(profileToFormData(initialProfile, initialToken));
      setLoadingData(false);
      return undefined;
    }
    const cached = peekProfileEditCache();
    if (cached) {
      setUserFormData(profileToFormData(cached.profile, cached.token));
      setLoadingData(false);
      return undefined;
    }
    let cancelled = false;
    fetch('/api/account/profile/edit', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      },
      credentials: 'same-origin'
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`profile edit GET ${res.status}`);
        }
        return res.json();
      })
      .then(response => {
        if (cancelled) return;
        if (!response?.data?.profile) {
          toast.error('Could not load your profile. Please refresh and try again.');
          return;
        }
        setProfileEditCache(response.data.profile, response.data._token);
        setUserFormData(profileToFormData(response.data.profile, response.data._token));
      })
      .catch(() => {
        toast.error('Could not load your profile. Please refresh and try again.');
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false);
      });
    return () => { cancelled = true; };
  }, [initialProfile, initialToken]);

  const hideNotification = () => setNotifications({});

  const clearFieldError = name => {
    if (!name) {
      return;
    }
    setFieldErrors(prev => {
      if (!prev[name]) {
        return prev;
      }
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleFormChange = e => {
    const { name, value } = e.target;
    const repeatType = e.target.dataset && e.target.dataset.repeatType;

    clearFieldError(name);

    setUserFormData(prev => ({
      ...prev,
      [name]: repeatType ? {
        ...(prev[name] || {}),
        [repeatType]: value
      } : value
    }));
  };

  const handlePhoneChange = e => {
    const { name, value } = e.target;
    clearFieldError(name);
    setUserFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFieldBlur = (name, error) => {
    setFieldErrors(prev => {
      const next = { ...prev };
      if (error) {
        next[name] = error;
      } else {
        delete next[name];
      }
      return next;
    });
  };

  const handleFormSubmit = e => {
    e.preventDefault();
    setNotifications({});
    const clientErrors = {};
    const phoneRegion = userFormData.country || userFormData.nationality;

    const requiredTextFields = [
      ['firstName', 'First name is required'],
      ['lastName', 'Last name is required'],
      ['nationality', 'Nationality is required'],
      ['passport', 'Passport is required'],
      ['birthCountry', 'Country of birth is required'],
      ['placeOfBirth', 'Place of birth is required'],
      ['email', 'Email is required'],
      ['phone', 'Phone number is required'],
      ['nextOfKinFirstName', 'Emergency contact first name is required'],
      ['nextOfKinLastName', 'Emergency contact last name is required'],
      ['nextOfKinRelationship', 'Emergency contact relationship is required'],
      ['city', 'City is required'],
      ['country', 'Country is required'],
      ['address', 'Address is required'],
      ['zip', 'Zip code is required']
    ];

    requiredTextFields.forEach(([field, message]) => {
      const value = (userFormData[field] ?? '').toString().trim();
      if (!value) {
        clientErrors[field] = message;
        return;
      }
      // A value the retention purge redacted is one we no longer hold. It is a non-empty string, so
      // the emptiness check above passed it and the profile could be saved with a passport, place of
      // birth or emergency contact reading '[REDACTED]'. Ask for it again instead. This form already
      // requires every field above to be present on save, so treating a redaction as absent asks
      // nothing of the guest that a genuinely empty field would not.
      const redactedError = validateNotRedacted(value);
      if (redactedError) {
        clientErrors[field] = redactedError;
      }
    });

    if (!userFormData.sex) {
      clientErrors.sex = 'Your sex is required';
    }

    if (!userFormData.birthDate?.day || !userFormData.birthDate?.month || !userFormData.birthDate?.year) {
      clientErrors.birthDate = 'Birth date is required';
    }

    const passportError = validatePassport(userFormData.passport, userFormData.nationality);
    const phoneError = validatePhone(userFormData.phone, phoneRegion);
    const nextOfKinPhoneError = !userFormData.nextOfKinPhone?.trim() ? 'Emergency contact phone is required' : validatePhone(userFormData.nextOfKinPhone, phoneRegion);
    if (passportError) {
      clientErrors.passport = passportError;
    }
    if (phoneError) {
      clientErrors.phone = phoneError;
    }
    if (!userFormData.nextOfKinPhone?.trim()) {
      clientErrors.nextOfKinPhone = 'Emergency contact phone is required';
    } else if (nextOfKinPhoneError) {
      clientErrors.nextOfKinPhone = nextOfKinPhoneError;
    }
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      setTimeout(() => scrollToFirstFieldError(Object.keys(clientErrors)), 0);
      return;
    }
    setFieldErrors({});
    setLoading(true);
    dispatch(updateUser(userFormData)).then(response => {
      setLoading(false);
      if (response.status === 'success') {
        setProfileEditCache(
          response.data?.profile || userFormData,
          userFormData._token
        );
        setNotifications({
          success: {
            notificationType: 'success',
            notificationMessage: response.message,
            showNotification: true
          }
        });
        Promise.resolve(dispatch(fetchAccountData())).finally(() => {
          if (onSuccess) {
            onSuccess(response);
          }
        });
      } else if (response.data && response.data.errors) {
        const flattenedErrors = flattenFormErrors(response.data.errors);
        const serverFieldErrors = {};
        const errors = {};
        flattenedErrors.forEach(({ field, message }) => {
          if (field !== '_form') {
            serverFieldErrors[field] = message;
          }
          errors[field] = {
            notificationType: 'error',
            notificationMessage: message,
            showNotification: true,
            onClose: hideNotification
          };
        });
        setFieldErrors(serverFieldErrors);
        const summaryMessage = flattenedErrors[0]?.message || 'Please correct the errors above.';
        setNotifications({
          error: {
            ...errors,
            summaryMessage
          }
        });
        setTimeout(() => scrollToFirstFieldError(flattenedErrors.map(({ field }) => field)), 0);
      }
    });
  };

  return loadingData ? (
    <div className="dui-text-center">
      <Svg
        name="sea-transport-wheel"
        className="d8-u-spinreverse-animation icon--xl dui-mt-4"
        fill="#1a3c70"
      />
      <p className="dui-my-4">Fetching profile...</p>
    </div>
  ) : (
    <form onSubmit={handleFormSubmit}>
      <fieldset className="dui-max-w-full dui-contents" disabled={loading} aria-busy={loading}>
        <EditProfileSection
          userFormData={userFormData}
          notifications={notifications}
          hideNotification={hideNotification}
          onChange={handleFormChange}
          onPhoneChange={handlePhoneChange}
          fieldErrors={fieldErrors}
          onFieldBlur={handleFieldBlur}
        />
        <EditContactSection
          userFormData={userFormData}
          onChange={handleFormChange}
          onPhoneChange={handlePhoneChange}
          notifications={notifications}
          fieldErrors={fieldErrors}
          onFieldBlur={handleFieldBlur}
          onEmailUpdated={profile => {
            setUserFormData(prev => ({
              ...prev,
              email: profile.email || prev.email
            }));
            dispatch(fetchAccountData());
          }}
        />
        <EditAddressSection
          userFormData={userFormData}
          notifications={notifications}
          hideNotification={hideNotification}
          onChange={handleFormChange}
          fieldErrors={fieldErrors}
          loading={loading}
        />
      </fieldset>
    </form>
  );
};

ProfileEditForm.propTypes = {
  onSuccess: PropTypes.func,
  initialProfile: PropTypes.object,
  initialToken: PropTypes.string
};

export default ProfileEditForm;
